import { ImageField } from '@sitecore-jss/sitecore-jss-vue/types/components/Image';
import { ISitecoreFormGenericInput } from '@/interfaces';
import { IRadioButtonCard } from '@/interfaces/entities/radio-button-card.interface';
import { IPromoCardMini } from '@/interfaces/entities/promo-card-mini.interface';
import { IPromoCardBasic } from '@/interfaces/entities/promo-card-basic.interface';
import { FormInputTypes } from '@/enums/form-input-types.enum';
import { FormKitBaseProps } from '@formkit/inputs';
import { MiniCardLayout, BasicCardLayout } from '@/enums/card-layout.enum';

export interface TooltipProps {
  title?: string;
  content: string;
  tooltipId: string;
  // Vue 2 migration alert: this code might not be required
  // this is an edge case where a '%' symbol is required before the tooltip trigger
  // not ideal, but Vue Formulate is very hard to modify otherwise
  contentBeforeTrigger?: string;
  tooltipOuterId?: string;
  isModal?: boolean;
}

export interface VueFormInputProps {
  label?: string;
  labelClass?: string;
  name: string;
  id?: string;
  type?: string;
  placeholder?: string;
  postContent?: string;
  preContent?: string;
  postContentClass?: string;
  preContentClass?: string;
  tooltip?: TooltipProps;
  format?: string;
  fields?: ISitecoreFormGenericInput[];
  emptyMessage?: string;
  validation?: any[][];
  validationMessages?: Record<string, string>;
  validationLabel?: string;
  validationVisibility?: FormKitBaseProps['validationVisibility'];
  validationRegex?: string;
  innerClass?: string;
  outerClass?: string;
  options?: IRadioButtonCard[];
  optionsClass?: string;
  defaultOption?: string;
  messagesClass?: string;
  messageClass?: string;
  help?: string;
  helpSm?: string;
  helpMd?: string;
  showPostContent?: boolean;
  loading?: boolean;
  refData?: string;
  value?: any;
  prefixIcon?: string;
  suffixIcon?: string;
  mask?: string;
  disablePaste?: boolean;
  pickerOnly?: boolean;
  radioGroupClass?: string;
  classesOptions?: string;
  decimals?: number;
  minDecimals?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  keepPlaceHolder?: boolean;
  showClearIcon?: boolean;
  minlength?: number;
  maxlength?: number;
  autocomplete?: string;
  inputmode?: string;
  inputInnerClass?: string;
  promoCards?: IPromoCardMini[] | IPromoCardBasic[];
  heading?: string;
  cardLayout?: MiniCardLayout | BasicCardLayout;
  maxItemsToShow?: number;
  deselect?: boolean;
  updatedFrequencyLabels?: string[];
  proRataSymbols?: (string|null)[] | null;
  amountSymbols?: (string|null)[] | null;
  marginLeftForAmountSymbol?: boolean | null;
  removeSpaceAfterProRataSymbol?: boolean | null;
}

function setupFormKitClass(vBindFormData: VueFormInputProps, formData: ISitecoreFormGenericInput) {
  if (formData?.innerClass?.value) {
    vBindFormData.innerClass = formData?.innerClass?.value;
  }
  if (formData?.outerClass?.value) {
    vBindFormData.outerClass = formData?.outerClass?.value;
  }
}

function setupValidation(vBindFormData: VueFormInputProps, formData: ISitecoreFormGenericInput) {
  /** Format Validation */
  if (formData?.isRequired?.value) {
    vBindFormData.validation = [...(vBindFormData.validation || []), ['required', 'trim']];

    if (formData?.isRequiredErrorMessage?.value) {
      vBindFormData.validationMessages = {
        ...(vBindFormData.validationMessages || {}),
        required: formData?.isRequiredErrorMessage?.value,
      };
    }
  }

  // Regex validation
  if (formData?.validationRegex?.value) {
    vBindFormData.validation = [
      ...(vBindFormData.validation || []),
      ['matches', new RegExp(formData.validationRegex.value)],
    ];

    vBindFormData.validationRegex = formData?.validationRegex?.value;

    if (formData?.validationRegexErrorMessage?.value) {
      vBindFormData.validationMessages = {
        ...(vBindFormData.validationMessages || {}),
        matches: formData?.validationRegexErrorMessage?.value,
      };
    }
  }

  // Custom validation
  if (formData?.validation?.value) {
    try {
      const params = new URLSearchParams(formData.validation.value);
      params.forEach((key, value) => {
        const payload: { message: string; regex?: string; minLength?: number; maxLength?: number } = JSON.parse(key);

        if (payload.regex) {
          vBindFormData.validation = [...(vBindFormData.validation || []), [value, new RegExp(payload.regex)]];
        } else if (payload.minLength && payload.maxLength) {
          vBindFormData.validation = [
            ...(vBindFormData.validation || []),
            ['length', payload.minLength, payload.maxLength],
          ];
        } else if (payload.minLength) {
          vBindFormData.validation = [...(vBindFormData.validation || []), ['length', payload.minLength]];
        } else if (payload.maxLength) {
          vBindFormData.validation = [...(vBindFormData.validation || []), ['length', 0, payload.maxLength]];
        } else {
          vBindFormData.validation = [...(vBindFormData.validation || []), [value]];
        }

        if (payload.message) {
          vBindFormData.validationMessages = {
            ...(vBindFormData.validationMessages || {}),
            [value.split(':')[0]]: payload.message,
          };
        }
      });
    } catch (error) {
      console.warn('Unable to format field validation: ', error);
    }
  }
}

function setupAck(vBindFormData: VueFormInputProps, formData: ISitecoreFormGenericInput) {
  // Must accept for checkbox ack
  if (formData?.mustAccept?.value) {
    vBindFormData.validation = [...(vBindFormData.validation || []), ['accepted']];

    vBindFormData.validationVisibility = 'dirty';

    if (formData?.mustAcceptErrorMessage?.value) {
      vBindFormData.validationMessages = {
        ...(vBindFormData.validationMessages || {}),
        accepted: formData?.mustAcceptErrorMessage?.value,
      };
    }
  }
}

/**
 * Convert a form field in Sitecore into an Object suitable for FormKit.
 *
 * @param formData
 * @param keyName
 * @returns
 */
export function convertSitecoreFormFieldToVueFormField(
  formData: ISitecoreFormGenericInput,
  keyName: string
): VueFormInputProps {
  const vBindFormData: VueFormInputProps = {
    label: formData?.label?.value ?? '',
    validationLabel: formData?.label?.value ?? '',
    name: formData?.formInputId?.value || 'FormInputId mising in SC - keyName:' + keyName,
    type: formData?.inputType?.value || 'FormInputType missing in SC - keyName:' + keyName,
    placeholder: formData?.placeholderText?.value ?? '',
    postContent: formData?.postContent?.value,
    preContent: formData?.preContent?.value,
    postContentClass: formData?.postContentClass?.value,
    preContentClass: formData?.preContentClass?.value,
    showPostContent: formData?.showPostContent,
    refData: formData?.refData?.value,
    prefixIcon: formData?.prefixIcon?.value,
    suffixIcon: formData?.suffixIcon?.value,
    help: formData?.helpXs?.value,
    helpSm: formData?.helpSm?.value,
    helpMd: formData?.helpMd?.value,
    disablePaste: formData?.disablePaste?.value,
    classesOptions: formData?.classesOptions?.value,
    decimals: formData?.decimals?.value,
    minDecimals: formData?.minDecimals?.value,
    min: formData?.min?.value,
    max: formData?.max?.value,
    showClearIcon: formData?.showClearIcon?.value,
    minlength: formData?.minlength?.value,
    maxlength: formData?.maxlength?.value,
    autocomplete: formData?.autocomplete?.value,
    inputInnerClass: formData?.inputInnerClass?.value,
  };

  setupFormKitClass(vBindFormData, formData);
  setupValidation(vBindFormData, formData);
  setupAck(vBindFormData, formData);

  if (formData?.options?.value) {
    const urlSearchParams = new URLSearchParams(formData?.options?.value?.replaceAll('+', '%2B'));
    const options = [];
    for (const [label, value] of urlSearchParams) {
      options.push({ label: label, value: value });
    }
    vBindFormData.options = options;
  } else if (formData?.inputType?.value === FormInputTypes.DROPDOWN) {
    vBindFormData.options = [];
    const dropdownFields = formData.fields as DropdownOption[];
    if (dropdownFields) {
      vBindFormData.options = dropdownFields.map((option) => {
        return {
          label: option.Label?.value ?? '',
          value: option.Value?.value,
          help: option.HelpText?.value,
          image: option.Image,
        };
      });
    }
    vBindFormData.deselect = false;
  } else if (formData?.inputType?.value === FormInputTypes.RADIO_CARD_LIST) {
    vBindFormData.options = convertSitecoreToVueRadioButtonCardOptions(formData);
    vBindFormData.optionsClass = formData?.optionsClass?.value;
  } else if (formData?.inputType?.value === FormInputTypes.CURRENCY) {
    if (formData.noDecimalsErrorMessage?.value) {
      vBindFormData.validationMessages = {
        ...(vBindFormData.validationMessages || {}),
        noDecimals: formData.noDecimalsErrorMessage.value,
      };
      vBindFormData.validation = [...(vBindFormData.validation || []), ['noDecimals']];
    }
  } else if (formData?.inputType?.value === FormInputTypes.PROMO_CARD_MINI_LIST) {
    vBindFormData.promoCards = convertSitecoreToVuePromoCardMiniItems(formData);
    vBindFormData.heading = formData?.heading?.value;
    vBindFormData.cardLayout = formData?.cardLayout?.value as MiniCardLayout;
    vBindFormData.maxItemsToShow = formData?.maxItemsToShow?.value;
  } else if (formData?.inputType?.value === FormInputTypes.PROMO_CARD_BASIC_LIST) {
    vBindFormData.promoCards = convertSitecoreToVuePromoCardBasicItems(formData);
    vBindFormData.heading = formData?.heading?.value;
    vBindFormData.cardLayout = formData?.cardLayout?.value as BasicCardLayout;
    vBindFormData.maxItemsToShow = formData?.maxItemsToShow?.value;
  }

  if (formData?.defaultOption?.value) {
    vBindFormData.defaultOption = formData?.defaultOption?.value;
  }

  if (formData?.tooltipContent?.value) {
    vBindFormData.tooltip = convertSitecoreToVueTooltip(formData);
  }

  if (formData?.emptyMessage?.value) {
    vBindFormData.emptyMessage = formData.emptyMessage.value;
  }
  if (formData?.mask?.value) {
    vBindFormData.mask = formData?.mask?.value;
  }
  if (formData?.defaultOption?.value) {
    vBindFormData.value = formData?.defaultOption?.value;
  }
  if (formData?.format?.value) {
    vBindFormData.format = formData?.format?.value;
  }
  if (formData?.pickerOnly?.value) {
    vBindFormData.pickerOnly = formData?.pickerOnly?.value;
  }

  return vBindFormData;
}

function convertSitecoreToVueRadioButtonCardOptions(formData: ISitecoreFormGenericInput): IRadioButtonCard[] {
  return (
    formData?.fields?.map((radioButtonCard: any) => ({
      value: radioButtonCard.value.value,
      label: radioButtonCard.label.value,
      help: radioButtonCard.help.value,
      image: radioButtonCard.image,
      callOut: radioButtonCard.callOut.value,
      rightLabel: radioButtonCard.rightLabel.value,
      rightHelp: radioButtonCard.rightHelp.value,
      visibleBody: radioButtonCard.visibleBody.value,
      hiddenBody: radioButtonCard.hiddenBody.value,
    })) ?? []
  );
}
// #endregion

function convertSitecoreToVuePromoCardMiniItems(formData: ISitecoreFormGenericInput): IPromoCardMini[] {
  return (
    formData?.fields?.map((promoCardMini: any) => ({
      icon: promoCardMini.icon,
      title: promoCardMini.title.value,
      description: promoCardMini.description.value,
      link: promoCardMini.link,
      showChevron: promoCardMini.showChevron.value,
      buttonStyle: promoCardMini.buttonStyle.value,
    })) ?? []
  );
}

function convertSitecoreToVuePromoCardBasicItems(formData: ISitecoreFormGenericInput): IPromoCardBasic[] {
  return (
    formData?.fields?.map((promoCardBasic: any) => ({
      icon: promoCardBasic.icon,
      title: promoCardBasic.title.value,
      subTitle: promoCardBasic.subTitle.value,
      description: promoCardBasic.description.value,
      leftButtonLink: promoCardBasic.leftButtonLink,
      leftButtonStyle: promoCardBasic.leftButtonStyle.value,
      leftButtonLabel: promoCardBasic.leftButtonLabel.value,
      rightButtonLink: promoCardBasic.rightButtonLink,
      rightButtonStyle: promoCardBasic.rightButtonStyle.value,
      rightButtonLabel: promoCardBasic.rightButtonLabel.value,
      includeBankContent: promoCardBasic.includeBankContent.value,
      leftMainLabel: promoCardBasic.leftMainLabel.value,
      leftMainLabelSuffix: promoCardBasic.leftMainLabelSuffix.value,
      leftSubLabel: promoCardBasic.leftSubLabel.value,
      rightMainLabel: promoCardBasic.rightMainLabel.value,
      rightMainLabelSuffix: promoCardBasic.rightMainLabelSuffix.value,
      rightSubLabel: promoCardBasic.rightSubLabel.value,
      footerText: promoCardBasic.footerText.value,
      openModalContent: promoCardBasic.openModalContent.value,
    })) ?? []
  );
}

// #region CheckboxList
interface CheckboxList {
  GroupName: { value: string };
  fields: CheckboxListOption[];
}

interface CheckboxListOption {
  Label?: { value: string };
  Value?: { value: string };
  HelpText?: { value: string };
}

interface DropdownOption {
  Label?: { value: string };
  Value?: { value: string };
  HelpText?: { value: string };
  Image?: ImageField;
}

function convertSitecoreToVueCheckboxListOptions(
  formData: ISitecoreFormGenericInput
): { label: string; value: any; help?: string }[] {
  const checkboxOptions: { label: string; value: any; help?: string }[] = [];
  if (formData?.inputType?.value === 'checkbox') {
    const checkboxList = formData as unknown as CheckboxList;

    return checkboxList.fields.map((checkboxListOption: CheckboxListOption) => {
      const cbItem: { label: string; value: any; help?: string } = {
        label: checkboxListOption.Label?.value ?? '',
        value: checkboxListOption.Value?.value,
        help: checkboxListOption.HelpText?.value,
      };

      return cbItem;
    });
  }

  return checkboxOptions;
}

function convertSitecoreToVueTooltip(formData: ISitecoreFormGenericInput): TooltipProps {
  return {
    title: formData?.tooltipModalTitle?.value,
    content: formData?.tooltipContent?.value,
    tooltipId: formData?.formInputId?.value + '__tooltip_id',
    tooltipOuterId: formData?.formInputId?.value + '__outer',
    isModal: formData?.tooltipShowInModal?.value,
  };
}

export function getAdobeTargetDataAttributes(input: Record<string, any>, stepName: string) {
  return {
    'data-component-type': input.name + '_' + input.id,
    'data-component-datasource': stepName,
    'data-test-id': input.name + '_' + input.id,
  };
}
// #endregion

export default function useFormInputData(formInputs: Record<string, ISitecoreFormGenericInput>) {
  /**
   * Create an Object with all VueFormulate fields mapped from Sitecore.
   *
   * @param {string} keyName The policy data payload key name (mapped as ID in Sitecore)
   * @param {number} index Use if you have multiple instances of the form input on the form, to set a unique ID
   */
  function formInputData(keyName: string, index?: number) {
    const formData = formInputs[keyName];
    const vBindFormData = convertSitecoreFormFieldToVueFormField(formData, keyName);

    // This is when we have a custom component that is a group of fields.
    // For example: Find your car is a group of inputs grouped by a "custom" input type
    if (formData?.inputType?.value === 'custom' && formData?.fields?.length) {
      vBindFormData.fields = formData.fields;
    }

    return vBindFormData;
  }

  return {
    formInputData,
    convertSitecoreToVueCheckboxListOptions,
    convertSitecoreToVueTooltip,
  };
}
